import React, { useEffect, useRef } from 'react'
import styles from './Filmstrip.module.scss'

type FilmStripProps = {
  images: ReadonlyArray<string>
  speed?: number
}

const FilmStrip: React.FC<FilmStripProps> = ({ images, speed = 0.5 }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)

  const allImages = [...images, ...images] // duplicated for looping
  const positionRef = useRef(0)
  const animationRef = useRef<number>(0)

  // Drag refs
  const dragStartX = useRef(0)
  const dragStartY = useRef(0)
  const dragStartOffset = useRef(0)
  const dragging = useRef(false)
  const draggingHorizontal = useRef(false)

  useEffect(() => {
    const track = trackRef.current
    if (!track) return

    const animate = () => {
      if (!dragging.current) {
        positionRef.current -= speed
        const width = track.scrollWidth / 2
        if (Math.abs(positionRef.current) >= width) {
          positionRef.current = 0
        }
        track.style.transform = `translateX(${positionRef.current}px)`
      }
      animationRef.current = requestAnimationFrame(animate)
    }

    animationRef.current = requestAnimationFrame(animate)
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current)
    }
  }, [speed])

  useEffect(() => {
    const container = containerRef.current
    const track = trackRef.current
    if (!container || !track) return

    const onTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0]
      dragStartX.current = touch.clientX
      dragStartY.current = touch.clientY
      dragStartOffset.current = positionRef.current
      dragging.current = true
      draggingHorizontal.current = false
    }

    const onTouchMove = (e: TouchEvent) => {
      if (!dragging.current) return
      const touch = e.touches[0]
      const dx = touch.clientX - dragStartX.current
      const dy = touch.clientY - dragStartY.current

      // Determine direction
      if (!draggingHorizontal.current && Math.abs(dx) > Math.abs(dy)) {
        draggingHorizontal.current = true
      }

      if (draggingHorizontal.current) {
        e.preventDefault() // now block vertical scroll
        positionRef.current = dragStartOffset.current + dx
        track.style.transform = `translateX(${positionRef.current}px)`
      }
      // else: do nothing — allow native scroll
    }

    const onTouchEnd = () => {
      dragging.current = false
      draggingHorizontal.current = false
    }

    container.addEventListener('touchstart', onTouchStart, { passive: true })
    container.addEventListener('touchmove', onTouchMove, { passive: false })
    container.addEventListener('touchend', onTouchEnd)
    container.addEventListener('touchcancel', onTouchEnd)

    return () => {
      container.removeEventListener('touchstart', onTouchStart)
      container.removeEventListener('touchmove', onTouchMove)
      container.removeEventListener('touchend', onTouchEnd)
      container.removeEventListener('touchcancel', onTouchEnd)
    }
  }, [])

  return (
    <div className={styles.filmstripContainer} ref={containerRef}>
      <div className={styles.filmstripTrack} ref={trackRef}>
        {allImages.map((url, i) => (
          <img key={i} src={url} className={styles.filmstripImage} alt={`img-${i}`} />
        ))}
      </div>
    </div>
  )
}

export default FilmStrip
